import React, { useCallback } from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { Typography, Wrapper } from '@screentone/core';
import classnames from 'classnames';
import { isEqual } from 'lodash';

// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { Article } from 'components/datamodel/content/Article/Article';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { MenuActionsKeys } from 'contexts/context-menu-actions/ContextMenuActionsContext';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useContextMenuActions } from 'contexts/context-menu-actions/useContextMenuActions';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelStableContext } from 'contexts/datamodel/useDataModel';
import { DRAGGABLE_PREFIXES, DROPPABLE_ID_PREFIXES } from 'contexts/drag-and-drop/dragUtils';
import { ArticleItem, CollectionDto } from 'data/generated/graphql';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import { safelyParseContent } from 'utils/temp';
import styles from './ManualItems.module.scss';

interface InnerListArticleProps {
  article: ArticleItem;
  index: number;
  listHierarchyId: string;
  fullHierarchyId: string;
  metadata?: CollectionDto['metadata'];
}

const InnerListArticle = React.memo(
  ({ article, index, listHierarchyId, fullHierarchyId, metadata }: InnerListArticleProps) => {
    const { renderActions } = useContextMenuActions();

    const renderFn = useCallback(
      () =>
        renderActions(MenuActionsKeys.DraftArticle, {
          entity: article,
          hierarchyId: fullHierarchyId,
          index,
          extraProperties: {
            allessehContent: safelyParseContent<AllessehContent>(article)
          }
        }),
      [article, fullHierarchyId, index, renderActions]
    );

    return (
      <Article
        collectionMetadata={metadata}
        data={article}
        parentHierarchyId={listHierarchyId}
        isDraft
        renderActions={renderFn}
        index={index}
      />
    );
  },
  (prevProps, nextProps) =>
    isEqual(prevProps.article, nextProps.article) &&
    prevProps.index === nextProps.index &&
    prevProps.listHierarchyId === nextProps.listHierarchyId &&
    prevProps.fullHierarchyId === nextProps.fullHierarchyId &&
    isEqual(prevProps.metadata, nextProps.metadata)
);

InnerListArticle.displayName = 'NewswiresManualItemsInnerListArticle';

interface InnerListProps {
  articles: ArticleItem[];
  listHierarchyId: string;
  metadata?: CollectionDto['metadata'];
}

const InnerList = React.memo(
  ({ articles, listHierarchyId, metadata }: InnerListProps) => {
    const { generateHierarchyId } = useDataModelStableContext();

    return (
      <>
        {articles.map((article, index) => {
          const fullHierarchyId = generateHierarchyId(article, listHierarchyId, index);
          return (
            <Draggable key={article.uuid} draggableId={DRAGGABLE_PREFIXES.COLLECTION_ITEM + article.uuid} index={index}>
              {(draggableProvided) => (
                <div
                  data-testid="curated-list-item"
                  ref={draggableProvided.innerRef}
                  {...draggableProvided.draggableProps}
                  {...draggableProvided.dragHandleProps}
                >
                  <InnerListArticle
                    article={article}
                    index={index}
                    listHierarchyId={listHierarchyId}
                    fullHierarchyId={fullHierarchyId}
                    metadata={metadata}
                  />
                </div>
              )}
            </Draggable>
          );
        })}
      </>
    );
  },
  (prevProps, nextProps) =>
    isEqual(prevProps.articles, nextProps.articles) && prevProps.listHierarchyId === nextProps.listHierarchyId
);

InnerList.displayName = 'NewswiresManualItemsInnerList';

interface ManualItemsProps {
  articles: ArticleItem[];
  listHierarchyId: string;
  metadata?: CollectionDto['metadata'];
  isActive?: boolean;
}

export const ManualItems = ({ articles, listHierarchyId, metadata, isActive = true }: ManualItemsProps) => (
  <Droppable droppableId={DROPPABLE_ID_PREFIXES.COLLECTION_ITEMS + listHierarchyId} isDropDisabled={!isActive}>
    {(droppableProvided, snapshot) => (
      <div
        data-testid="curated-list-container"
        className={classnames(styles.itemsList, { [styles.itemsListEmpty]: articles.length === 0 })}
        ref={droppableProvided.innerRef}
        {...droppableProvided.droppableProps}
      >
        {articles.length === 0 ? (
          <Wrapper
            data-testid="curated-list-empty-list"
            className={classnames(styles.emptyCuratedList, {
              [styles.emptyCuratedListDragging]: snapshot.isDraggingOver
            })}
            padding={{ all: 'md' }}
          >
            <Typography data-testid="curated-list-empty-label" variant="note">
              Drop story here
            </Typography>
          </Wrapper>
        ) : (
          <InnerList articles={articles} listHierarchyId={listHierarchyId} metadata={metadata} />
        )}
        {droppableProvided.placeholder}
      </div>
    )}
  </Droppable>
);
